import { settingsWords as words, settingsText } from "./settings-copy";
import { useQueryClient } from "@tanstack/react-query";
import type { Box, Read } from "@/components/ui/box.schema";
import { settingsApi, type InstitutionSettings } from "@/lib/api/settings";
import { imam, resultState, useSettingsCommit } from "./settings-boxes";

export function useRecordingSigninBox(
  read: Read<InstitutionSettings>,
  rank: number | null = null,
): Box & { onOpen: () => void } {
  const client = useQueryClient();
  const outcome = useSettingsCommit();
  const value =
    read.status === "ready"
      ? read.value
      : read.status === "stale"
        ? read.last
        : undefined;
  const signin = value?.recording_signin;
  const attested = Boolean(signin?.attested);
  return {
    onOpen: outcome.reset,
    id: "recording-signin",
    job: words.recordOrClearTheRecordingSignInAttestation,
    kind: "task",
    label: words.recordingSignIn,
    owner: imam,
    read: value && !signin ? { status: "forbidden" } : read,
    fold: "settled",
    draft: false,
    result: outcome.result,
    detail: () =>
      attested
        ? settingsText.attestation(
            signin?.attested_by_name,
            signin?.attested_at,
          )
        : words.recordingLinksStayNotPublishedUntilThisIsAttested,
    state: () =>
      resultState(outcome.result, {
        class: attested ? "done" : "waiting",
        word: attested ? words.attested : words.notAttested,
      }),
    act: {
      verb: attested ? words.clear : words.attestTheSetting,
      does: "open",
    },
    exits: attested ? [{ label: words.clear, reopens: true }] : undefined,
    candidate: { eligible: true, blocked: null, rank: attested ? null : rank },
    parts: [
      {
        id: "signin-setting",
        label: words.zoomSSetting,
        state: { class: "reading", word: words.iMAMCanTReadIt },
        owner: { system: "zoom", readAt: null },
        fold: "never",
        draft: "none",
        render: () => (
          <p className="text-ui">
            {attested
              ? words.clearOnlyIfTheRecordingSignInSettingInZoomWasUndone
              : words.inZoomRequireSignInOnRecordingsBeforeAttestingHere}
          </p>
        ),
      },
    ],
    commit: {
      verb: attested ? words.clearAttestation : words.attestTheSetting,
      cap: attested
        ? words.whenYouClearTheAttestation
        : words.whenYouAttestTheSetting,
      effects: () => [
        {
          id: "publish",
          sentence: attested
            ? words.publishedRecordingsStayPublishedFutureRecordingLinksWaitForAttestation
            : words.recordingLinksCanPublishAfterTheInstructorReviewsThem,
        },
      ],
      blockers: () => [],
      run: (key) => {
        const effects = [
          attested
            ? words.attestationClearedPublishedRecordingsStayPublished
            : words.recordingSignInAttestedRecordingLinksCanNowPublish,
        ];
        return outcome.run(
          key,
          async (id) => {
            const next = await settingsApi.attestRecordingSignin(!attested, id);
            client.setQueryData<InstitutionSettings>(
              ["admin", "settings"],
              (current) =>
                current
                  ? { ...current, recording_signin: next.recording_signin }
                  : current,
            );
            void client.invalidateQueries({ queryKey: ["tenant", "me"] });
            void client.invalidateQueries({
              queryKey: ["admin", "settings-ranking"],
            });
          },
          effects,
          async () => {
            await client.invalidateQueries({ queryKey: ["admin", "settings"] });
          },
        );
      },
    },
  };
}
